/**
 * What a broker is told when OUR SOFTWARE, not a lender, is why a name is missing.
 *
 * Synthesis on the parallel path asks one model per lender, and a call can die of things
 * that have nothing to do with the loan: an opaque 400, an error thrown from inside an
 * open stream, a timeout, an output budget the answer did not fit in. Every one of those
 * is already retried; this file is about the ones still dead afterwards. Until now they
 * were counted and dropped, so a lender lost to a transport error and a lender that
 * declined the file arrived at the broker in the same shape - which is to say, as nothing.
 *
 * THE DISTINCTION IS THE PRODUCT. Henry, on the requirement: "we want clear delineation
 * of system-failures vs. actual business logic failures/model failures." A decline is an
 * answer about the loan and the broker acts on it. A system failure is an answer about
 * us, it says nothing about the loan, and the only correct response to it is to run the
 * search again. Presenting the second as the first is how a broker stops calling a lender
 * who would have funded the deal.
 *
 * IT LIVES IN shared, AND THAT IS DELIBERATE. The results page renders JSX, the digest
 * renders HTML in Henry's mail client, and both have to say the same thing about the same
 * failure. This package already learned that lesson expensively - notify.ts kept its own
 * copy of the four-slot rule until the mail and the page named a different four lenders
 * on 2026-09-02, which is why `slotAnswer` is here rather than there. One sentence, one
 * definition, two renderers.
 *
 * It carries no reason string. The cause reaches the server log and the stats sink, where
 * somebody can act on it; "Stream.iterator: overloaded_error" in front of a broker is
 * noise attached to advice they cannot use.
 */

/** A lender the sweep could not finish, as a surface needs to name it. */
export interface LostLender {
  readonly lenderSlug: string;
  /** What the broker calls them. Never a slug and never a uuid. */
  readonly lenderName: string;
}

/**
 * The notice, in parts, because the two renderers lay it out differently.
 *
 * `text` is the whole of it in one string, for any channel with no layout to give it -
 * a log line, a plain-text mail, a test asserting the wording exists somewhere. The page
 * and the digest use the parts.
 */
export interface SystemFailureNotice {
  /** Said first, and it says which of the two kinds of missing this is. */
  readonly title: string;
  /** Names the lenders and states what their absence does and does not mean. */
  readonly detail: string;
  /** What to do about it. There is only ever one thing. */
  readonly action: string;
  /** The display names, in roster order, deduplicated. */
  readonly lenderNames: readonly string[];
  readonly text: string;
}

/** "Arc Home", "Arc Home and Verus", "Arc Home, Verus and Vista". */
function nameList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`;
}

/**
 * The notice for this answer, or null when nothing failed.
 *
 * Null rather than an empty object so a caller cannot render an empty banner by
 * forgetting to check, which is the mistake `droppedFindings > 0` makes possible one
 * card further down the same page.
 *
 * Deduplicated on the slug: one lender is one line however many of its calls died, and
 * a name printed twice reads as two banks.
 */
export function systemFailureNotice(
  lost: readonly LostLender[],
): SystemFailureNotice | null {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const l of lost) {
    const key = l.lenderSlug || l.lenderName;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    names.push(l.lenderName || l.lenderSlug);
  }
  if (names.length === 0) return null;

  const one = names.length === 1;
  const title = 'System error — this answer is incomplete';
  const detail =
    `We could not finish checking ${nameList(names)}. That is a failure in our software, `
    + `not an answer about this loan — ${one ? 'it is' : 'they are'} missing from these `
    + `results rather than declined.`;
  const action = 'Run this search again before acting on it.';
  return { title, detail, action, lenderNames: names, text: `${title}. ${detail} ${action}` };
}
